import React from 'react';
import type { EmployeeSuperValidation, ImportLine, XeroEmployee } from '../types';

interface SummaryPanelProps {
  lines: ImportLine[];
  employees: XeroEmployee[];
  superValidations: Record<string, EmployeeSuperValidation>;
  reportMonth: string | null;
  reportYear: string | null;
  onCreatePayRun: () => void;
  onShowQuickSuper: () => void;
  canCreate: boolean;
  selectionLimit: number;
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function SummaryPanel({
  lines,
  employees,
  superValidations,
  reportMonth,
  reportYear,
  onCreatePayRun,
  onShowQuickSuper,
  canCreate,
  selectionLimit,
}: SummaryPanelProps) {
  const totalDebit = lines.reduce((sum, l) => sum + parseFloat(l.debitTotal), 0);
  const totalSuper = lines.reduce((sum, l) => sum + parseFloat(l.superAmount), 0);
  const overLimit = lines.length > selectionLimit;
  const unmappedCount = lines.filter((l) => !l.xeroEmployeeId).length;
  const contractorCount = new Set(
    lines.map((line) => line.xeroEmployeeId || `unmapped:${line.id}`),
  ).size;

  // Mapped lines whose full employee detail confirms no valid super setup
  const noSuperLines = lines.filter((l) => {
    if (!l.xeroEmployeeId) return false;
    return superValidations[l.xeroEmployeeId]?.superStatus === 'MISSING';
  });

  const checkingSuperLines = lines.filter((l) => {
    if (!l.xeroEmployeeId) return false;
    const status = superValidations[l.xeroEmployeeId]?.superStatus ?? 'UNKNOWN';
    return status === 'UNKNOWN' || status === 'CHECKING';
  });

  const superErrorLines = lines.filter((l) => {
    if (!l.xeroEmployeeId) return false;
    return superValidations[l.xeroEmployeeId]?.superStatus === 'ERROR';
  });

  return (
    <div className="mt-4 space-y-4">
      {/* Totals row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total DEBIT" value={`$${fmt(totalDebit)}`} color="gray" />
        <StatCard label="Total Super" value={`$${fmt(totalSuper)}`} color="gray" />
        <StatCard label="Super payments" value={String(contractorCount)} color="gray" />
        <StatCard
          label="Unmapped"
          value={String(unmappedCount)}
          color="gray"
        />
      </div>

      <p className="text-xs text-slate-500">
        {lines.length} contractor{lines.length === 1 ? '' : 's'} selected for Xero.
      </p>

      {overLimit && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              {lines.length} contractors are ticked, but Xero can take up to {selectionLimit}
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              Untick {lines.length - selectionLimit} to continue. Unticked contractors are paid through QuickSuper.
            </p>
          </div>
        </div>
      )}

      {/* Warning if unmapped */}
      {unmappedCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-800">
              {unmappedCount} contractor{unmappedCount > 1 ? 's' : ''} still need{unmappedCount === 1 ? 's' : ''} mapping
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Select the matching Xero employee from the dropdown for each unmapped row before creating the pay run.
            </p>
          </div>
        </div>
      )}

      {checkingSuperLines.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-800">
              Checking super fund details for {checkingSuperLines.length} employee{checkingSuperLines.length > 1 ? 's' : ''}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              The app will only show a missing super warning after full employee detail has been checked.
            </p>
          </div>
        </div>
      )}

      {/* Error if any mapped employee is missing super setup */}
      {superErrorLines.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-800">
              Could not verify super fund details for {superErrorLines.length} employee{superErrorLines.length > 1 ? 's' : ''}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Retry the super check before creating the pay run.
            </p>
          </div>
        </div>
      )}

      {noSuperLines.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-800">
              Super fund not configured for {noSuperLines.length} employee{noSuperLines.length > 1 ? 's' : ''}
            </p>
            <ul className="mt-1 space-y-1">
              {noSuperLines.map((l) => {
                const emp = employees.find((e) => e.EmployeeID === l.xeroEmployeeId);
                const name = emp ? `${emp.FirstName} ${emp.LastName}` : l.xeroEmployeeName ?? l.xeroEmployeeId;
                return (
                  <li key={l.id} className="text-xs text-slate-500">
                    <span className="font-semibold">{name}</span> - go to{' '}
                    <span className="font-mono">Payroll / Employees / {name} / Superannuation</span>{' '}
                    in Xero and add a super fund and member number, then return and refresh.
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-950">
            Create Xero Draft Pay Run
            {reportMonth && reportYear ? ` - ${reportMonth} ${reportYear}` : ''}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Total super: <span className="font-semibold text-slate-700">${fmt(totalSuper)}</span> across {contractorCount} super payment{contractorCount !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={onCreatePayRun}
          disabled={!canCreate}
          className="btn-primary flex-shrink-0"
        >
          {canCreate ? (
            <>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
              Create Xero Draft Pay Run
            </>
          ) : (
            <>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {overLimit
                ? `Untick to ${selectionLimit} or fewer`
                : lines.length === 0
                ? 'Select contractors'
                : unmappedCount > 0
                ? 'Map all contractors first'
                : checkingSuperLines.length > 0
                ? 'Checking super funds'
                : 'Fix super setup first'}
            </>
          )}
        </button>
      </div>

      <button onClick={onShowQuickSuper} className="btn-secondary w-full text-sm">
        Show QuickSuper Contributions
      </button>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'gray';
}) {
  const colors = {
    gray: 'bg-white border-slate-200',
  };
  const valueColors = {
    gray: 'text-slate-950',
  };

  return (
    <div className={`rounded-lg border px-4 py-3.5 ${colors[color]}`}>
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${valueColors[color]}`}>{value}</p>
    </div>
  );
}
